import { useState } from 'react';
import { Link } from 'react-router-dom';
import { PremiumHeader } from '@/components/premium/PremiumHeader';
import { SEO } from '@/components/SEO';
import { MapPin, ArrowRight, Building2, Calendar, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PageFAQ } from '@/components/PageFAQ';
import { RelatedPages } from '@/components/RelatedPages';

// Projects data - will be populated as user shares project details
export const PROJECTS_DATA = [
  {
    id: 1,
    slug: "odyssey-logistics-office-fitout",
    name: "Modern Modular Office Fitout for Odyssey Logistics",
    client: "Odyssey Logistics Pvt. Ltd.",
    location: "Mumbai",
    completionDate: "2024",
    teamSize: "50+ employees",
    description: "This project showcases a complete modular office transformation delivered for Odyssey Logistics Pvt. Ltd., converting an empty shell into a modern, efficient workspace. The design focused on ergonomic workstations, smart storage solutions, and acoustic privacy while maintaining an open and collaborative layout.",
    challenge: "Odyssey Logistics required a complete workplace transformation to support their growing operations and evolving team structure. The existing space was a bare shell lacking defined work zones, acoustic privacy, and efficient storage. Key challenges included no functional workstation layout, lack of collaborative and private work areas, inefficient space utilization, absence of modern corporate aesthetics, and need for quick execution with minimal disruption.",
    solution: "We designed and delivered a turnkey modular office setup tailored to operational efficiency and future scalability. Our approach included ergonomically designed modular workstations, integrated storage solutions for optimized space usage, acoustic phone booths for private conversations, glass-partitioned cabins to maintain openness, clean neutral finishes for a modern corporate look, and efficient layout planning to maximize seating capacity.",
    beforeImage: "https://customer-assets.emergentagent.com/job_dedcadd1-3349-43fc-af7f-bc3d9d567fd1/artifacts/7u7z6qu2_Odyssey%20Logistics%20Pvt.%20Ltd.%20Before%20Pic.png",
    afterImage: "https://customer-assets.emergentagent.com/job_dedcadd1-3349-43fc-af7f-bc3d9d567fd1/artifacts/160p2tkh_Odyssey%20Logistics%20Pvt.%20Ltd.%20After%20Pic.png",
    beforeCaption: "A bare shell workspace with no defined layout, highlighting the need for structured planning and functional design to support business operations.",
    afterCaption: "A modern modular office transformation featuring ergonomic workstations, integrated storage, and collaborative spaces designed for efficiency and growth.",
    highlights: [
      "Increased seating capacity with better circulation",
      "Enhanced employee productivity and comfort",
      "Strong corporate visual identity",
      "Flexible workspace ready for scaling",
      "Modern, premium office ambience",
      "Ergonomic workstations with integrated storage",
      "Acoustic phone booths for privacy",
      "Glass-partitioned cabins"
    ],
    gallery: []
  },
  {
    id: 2,
    slug: "sky-industries-high-density-office",
    name: "High Density Modular Office Workstation Setup for Sky Industries Limited",
    client: "Sky Industries Limited",
    location: "Mumbai",
    completionDate: "2024",
    teamSize: "100+ employees",
    description: "This project showcases the transformation of a bare shell commercial office into a fully functional high-density modular workspace for Sky Industries Limited. The objective was to design a scalable office layout that supports large operational teams while maintaining comfort and workflow efficiency.",
    challenge: "Sky Industries Limited required a complete workspace upgrade to support a large operational team in a high-density environment. The existing office had an empty bare shell with exposed services, no functional layout planning, lack of defined work zones, no collaborative or meeting areas, and inefficient space utilization potential. The client needed a fast, scalable modular solution that could accommodate a large workforce while maintaining workflow efficiency and visual order.",
    solution: "We designed and executed a high-density modular workstation layout tailored to operational workflows and scalability. Key interventions included space planning for large team seating capacity, installation of modular workstations with privacy panels, integrated cable management for clean desk environment, ergonomic seating layout for long working hours, defined circulation pathways for smooth movement, and optimized lighting integration with existing services.",
    beforeImage: "https://customer-assets.emergentagent.com/job_dedcadd1-3349-43fc-af7f-bc3d9d567fd1/artifacts/huy1p8sb_Sky%20Industries%20Ltd.%20Before%20Pic.png",
    afterImage: "https://customer-assets.emergentagent.com/job_dedcadd1-3349-43fc-af7f-bc3d9d567fd1/artifacts/mlb07q11_Sky%20Industries%20Ltd.%20After%20pic.png",
    beforeCaption: "Bare shell commercial floor with exposed services and no functional workspace layout, ready for planning and fit-out.",
    afterCaption: "High-density modular workstation office designed for operational efficiency with optimized layout and ergonomic seating.",
    highlights: [
      "Significant increase in usable seating capacity",
      "Clean, professional operational floor",
      "Improved workflow efficiency",
      "Better team collaboration through organized zoning",
      "Scalable modular setup for future expansion",
      "Modular workstations with privacy panels",
      "Integrated cable management",
      "Ergonomic seating for long working hours"
    ],
    gallery: []
  },
  {
    id: 3,
    slug: "royal-pharmaceuticals-modular-office",
    name: "Modern Modular Office Fitout for Royal Pharmaceuticals Pvt. Ltd.",
    client: "Royal Pharmaceuticals Pvt. Ltd.",
    location: "Chandigarh",
    completionDate: "2024",
    teamSize: "75+ employees",
    description: "This project highlights the transformation of an open commercial floor into a fully functional modular office workspace for Royal Pharmaceuticals Pvt. Ltd. The objective was to create a modern, scalable office environment that supports team productivity while maintaining a professional corporate aesthetic.",
    challenge: "Royal Pharmaceuticals Pvt. Ltd. needed to convert an open commercial floor into a functional, modern office environment that could support growing teams while maintaining a clean and professional corporate look. The existing space had a large open floor with no structured workspace layout, lack of defined departments or work zones, no collaborative or meeting spaces, underutilized floor potential, and absence of modern office infrastructure. The client required a smart modular setup that could be implemented quickly with flexibility for future expansion.",
    solution: "We delivered a comprehensive workspace transformation using modular office systems designed for functionality, aesthetics, and scalability. Key interventions included strategic space planning for optimal seat allocation, installation of modular workstations with privacy panels, creation of glass-partition cabins and meeting rooms, integration of phone booths for focused work, ergonomic seating layout for employee comfort, clean cable management for clutter-free desks, and indoor greens to enhance workplace ambience.",
    beforeImage: "https://customer-assets.emergentagent.com/job_dedcadd1-3349-43fc-af7f-bc3d9d567fd1/artifacts/quj914fx_Royal%20Pharmaceuticals%20Pvt.%20Ltd.%20Before%20Pic.jpeg",
    afterImage: "https://customer-assets.emergentagent.com/job_dedcadd1-3349-43fc-af7f-bc3d9d567fd1/artifacts/2ynajfup_Royal%20Pharmaceuticals%20Pvt.%20Ltd.%20After%20Pic.jpeg",
    beforeCaption: "Open commercial floor with minimal infrastructure and no defined workspace layout, ready for transformation.",
    afterCaption: "Modern modular office with structured workstations, glass cabins, and collaborative zones designed for productivity and scalability.",
    highlights: [
      "Efficient use of floor space with increased seating capacity",
      "Professional and collaborative workspace environment",
      "Improved employee productivity and comfort",
      "Clear zoning for teams and management functions",
      "Future-ready modular infrastructure",
      "Glass-partition cabins and meeting rooms",
      "Phone booths for focused work",
      "Indoor greens for enhanced ambience"
    ],
    gallery: []
  },
  {
    id: 4,
    slug: "safe-express-modular-office-fitout",
    name: "Modern Modular Office Fit-out for Safe Express",
    client: "Safe Express",
    location: "Pune",
    completionDate: "2024",
    teamSize: "150+ employees",
    description: "Modern modular office fit-out completed for Safe Express featuring high-density workstations, ergonomic seating, and collaborative pods. The project transformed a bare commercial shell into a scalable corporate workspace using modular office furniture systems designed for flexibility, productivity, and efficient space utilization.",
    challenge: "The project began with a large open bare workspace with expansive glazing and minimal interior infrastructure. The client required a high-capacity office setup that could accommodate large teams while maintaining visual openness, ergonomic comfort, and acoustic balance. The challenge was to create a scalable workstation layout without overcrowding the floor or blocking natural light.",
    solution: "A modular workstation ecosystem was designed and installed to maximize seating density while maintaining clean circulation paths. Linear benching systems with acoustic privacy panels were introduced to create structured work zones. Ergonomic seating, integrated cable management, and coordinated finishes ensured a cohesive and efficient workspace. Collaborative pods were incorporated to support focused discussions without disrupting the open plan.",
    beforeImage: "https://customer-assets.emergentagent.com/job_dedcadd1-3349-43fc-af7f-bc3d9d567fd1/artifacts/wao9uw17_Safe%20Express%20Before%20Pic.jpeg",
    afterImage: "https://customer-assets.emergentagent.com/job_dedcadd1-3349-43fc-af7f-bc3d9d567fd1/artifacts/ftkni76o_Safe%20Express%20After%20Pic.jpeg",
    beforeCaption: "A large open shell space with expansive windows and minimal infrastructure, ready for workplace planning and modular fit-out.",
    afterCaption: "A fully operational modular office with high-density workstations, acoustic panels, and collaborative zones designed for productivity and scalability.",
    highlights: [
      "Vibrant, high-efficiency workspace",
      "Preserved openness and natural light",
      "Flexible layout for future expansion",
      "Improved workflow organization",
      "Professional corporate environment",
      "Linear benching with acoustic panels",
      "Collaborative pods for focused discussions",
      "Integrated cable management"
    ],
    gallery: []
  }
];

export default function Projects() {
  const [filter, setFilter] = useState('all');

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Delivered Workspaces | Modular Furniture Store"
        description="Explore our portfolio of office transformation projects. See how we've helped businesses across India create productive, modern workspaces with our modular furniture solutions."
        canonical="/projects"
      />
      <PremiumHeader />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-[#f8f7f4]">
        <div className="container mx-auto px-4 sm:px-8">
          <div className="max-w-3xl">
            <p className="text-[#c5a059] text-sm tracking-[0.3em] uppercase mb-4 font-medium">
              Our Work
            </p>
            <h1 
              className="text-4xl sm:text-5xl md:text-6xl font-bold text-[#1a1a1a] mb-6"
              
            >
              Workspace Transformations
            </h1>
            <p className="text-gray-600 text-lg sm:text-xl leading-relaxed">
              Discover how we've helped businesses across India transform their workspaces 
              into productive, inspiring environments. Each project showcases our commitment 
              to quality, innovation, and client satisfaction.
            </p>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-[#c5a059] py-6">
        <div className="container mx-auto px-4 sm:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div>
              <p className="text-3xl font-bold text-[#1a1a1a]">500+</p>
              <p className="text-sm text-[#1a1a1a]/80">Projects Completed</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-[#1a1a1a]">50+</p>
              <p className="text-sm text-[#1a1a1a]/80">Cities Served</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-[#1a1a1a]">10+</p>
              <p className="text-sm text-[#1a1a1a]/80">Years Experience</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-[#1a1a1a]">98%</p>
              <p className="text-sm text-[#1a1a1a]/80">Client Satisfaction</p>
            </div>
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-16">
        <div className="container mx-auto px-4 sm:px-8">
          {PROJECTS_DATA.length === 0 ? (
            /* Empty State */
            <div className="text-center py-20">
              <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-6" />
              <h2 className="text-2xl font-bold text-[#1a1a1a] mb-4">
                Projects Coming Soon
              </h2>
              <p className="text-gray-600 max-w-md mx-auto mb-8">
                We're currently documenting our latest workspace transformations. 
                Check back soon to see our portfolio of successful projects.
              </p>
              <Link to="/contact">
                <Button className="bg-[#c5a059] hover:bg-[#b8934d] text-[#1a1a1a]">
                  Discuss Your Project
                </Button>
              </Link>
            </div>
          ) : (
            /* Projects Grid */
            <div className="grid md:grid-cols-2 gap-8">
              {PROJECTS_DATA.map((project) => (
                <Link 
                  key={project.id}
                  to={`/projects/${project.slug}`}
                  className="group"
                  data-testid={`project-card-${project.slug}`}
                >
                  <div className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100">
                    {/* Image Comparison Preview */}
                    <div className="relative aspect-[16/10] overflow-hidden">
                      <img 
                        src={project.afterImage}
                        alt={`${project.name} - After transformation`}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                      
                      {/* Before Image Peek */}
                      <div className="absolute left-0 top-0 bottom-0 w-1/4 overflow-hidden border-r-4 border-white">
                        <img 
                          src={project.beforeImage}
                          alt={`${project.name} - Before`}
                          className="w-full h-full object-cover object-left"
                          style={{ width: '400%' }}
                        />
                        <div className="absolute inset-0 bg-black/30" />
                        <span className="absolute top-4 left-4 bg-black/70 text-white text-xs px-2 py-1 rounded">
                          BEFORE
                        </span>
                      </div>
                      
                      {/* After Label */}
                      <span className="absolute top-4 right-4 bg-[#c5a059] text-[#1a1a1a] text-xs px-2 py-1 rounded font-medium">
                        AFTER
                      </span>

                      {/* View Project CTA */}
                      <div className="absolute bottom-4 right-4 bg-white text-[#1a1a1a] px-4 py-2 rounded-full text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center gap-2">
                        View Project <ArrowRight className="w-4 h-4" />
                      </div>
                    </div>

                    {/* Project Info */}
                    <div className="p-6">
                      <h3 className="text-xl font-bold text-[#1a1a1a] mb-2 group-hover:text-[#c5a059] transition-colors">
                        {project.name}
                      </h3>
                      <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                        {project.description}
                      </p>
                      <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                        <div className="flex items-center gap-1">
                          <MapPin className="w-4 h-4 text-[#c5a059]" />
                          <span>{project.location}</span>
                        </div>
                        {project.completionDate && (
                          <div className="flex items-center gap-1">
                            <Calendar className="w-4 h-4 text-[#c5a059]" />
                            <span>{project.completionDate}</span>
                          </div>
                        )}
                        {project.teamSize && (
                          <div className="flex items-center gap-1">
                            <Users className="w-4 h-4 text-[#c5a059]" />
                            <span>{project.teamSize}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#f8f7f4]">
        <div className="container mx-auto px-4 sm:px-8 text-center">
          <h2 
            className="text-3xl sm:text-4xl font-bold text-[#1a1a1a] mb-4"
            
          >
            Ready to Transform Your Workspace?
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto mb-8">
            Let's discuss how we can create the perfect workspace for your team. 
            Our experts will guide you through every step of the transformation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/bulk-inquiry">
              <Button className="bg-[#c5a059] hover:bg-[#b8934d] text-[#1a1a1a] px-8">
                Request Consultation
              </Button>
            </Link>
            <Link to="/products">
              <Button variant="outline" className="border-[#1a1a1a] text-[#1a1a1a] px-8">
                Browse Products
              </Button>
            </Link>
          </div>
        </div>
      </section>
      {/* FAQ + Interlinks */}
      <div className="container mx-auto px-4 sm:px-8 py-12 bg-white">
        <PageFAQ pageKey="projects" />
        <RelatedPages pageKey="projects" className="mt-12" />
      </div>
    </div>
  );
}
